import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, LogOut, ArrowLeft, Smartphone, Bell, Download, Wifi, Palette, Save, Loader2, ToggleLeft, ToggleRight, Eye } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';

export default function AdminPWASettings() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    enabled: true,
    install_prompt_enabled: true,
    install_prompt_title: 'Install OnsiteHand',
    install_prompt_description: 'Add OnsiteHand to your home screen for quick access',
    install_prompt_button_text: 'Install App',
    offline_enabled: true,
    push_notifications_enabled: true,
    app_name: 'OnsiteHand',
    app_short_name: 'OnsiteHand',
    app_description: 'Connect with qualified UK tradespeople',
    theme_color: '#F97316',
    background_color: '#FEF3C7'
  });

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      
      if (response.data.user_type !== 'admin') {
        navigate('/login');
        return;
      }
      
      setUser(response.data);
      fetchSettings();
    } catch (error) {
      navigate('/login');
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/pwa/settings`);
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Failed to fetch PWA settings');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      navigate('/login');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  const handleToggle = (field) => {
    setSettings(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/pwa/settings`, settings, {
        withCredentials: true
      });
      toast.success('PWA settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const Toggle = ({ enabled, onToggle, label }) => (
    <button
      type="button"
      onClick={onToggle}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
        enabled ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
      }`}
    >
      {enabled ? <ToggleRight className="h-5 w-5" /> : <ToggleLeft className="h-5 w-5" />}
      <span className="font-medium">{enabled ? 'On' : 'Off'}</span>
    </button>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="text-slate-600 hover:text-slate-900 transition-colors"
                data-testid="back-btn"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="flex items-center gap-2">
                <Building2 className="h-8 w-8 text-orange-500" />
                <h1 className="text-xl font-bold text-slate-900">PWA Settings</h1>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="h-5 w-5" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <Smartphone className="h-5 w-5 text-blue-500 mt-0.5" />
            <div>
              <p className="text-blue-800 font-medium">Progressive Web App (PWA)</p>
              <p className="text-blue-700 text-sm mt-1">
                Configure how OnsiteHand appears and behaves when installed on mobile devices. 
                Users can add the app to their home screen for a native app-like experience.
              </p>
            </div>
          </div>
        </div>

        {/* Master Toggle */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <Smartphone className="h-5 w-5 text-orange-500" />
                PWA Features
              </h2>
              <p className="text-slate-600 text-sm mt-1">
                Enable or disable all PWA features globally
              </p>
            </div>
            <Toggle 
              enabled={settings.enabled} 
              onToggle={() => handleToggle('enabled')}
            />
          </div>
        </div>

        {settings.enabled && (
          <>
            {/* Install Prompt Settings */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                    <Download className="h-5 w-5 text-green-500" />
                    Install Prompt
                  </h3>
                  <p className="text-slate-600 text-sm mt-1">
                    Show a banner prompting users to install the app
                  </p>
                </div>
                <Toggle 
                  enabled={settings.install_prompt_enabled} 
                  onToggle={() => handleToggle('install_prompt_enabled')}
                />
              </div>

              {settings.install_prompt_enabled && (
                <div className="space-y-4 pt-4 border-t border-slate-200">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Prompt Title
                    </label>
                    <input
                      type="text"
                      name="install_prompt_title"
                      value={settings.install_prompt_title}
                      onChange={handleChange}
                      className="w-full"
                      data-testid="install-prompt-title"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Prompt Description
                    </label>
                    <textarea
                      name="install_prompt_description"
                      value={settings.install_prompt_description}
                      onChange={handleChange}
                      rows={2}
                      className="w-full"
                      data-testid="install-prompt-description"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Button Text
                    </label>
                    <input
                      type="text"
                      name="install_prompt_button_text"
                      value={settings.install_prompt_button_text}
                      onChange={handleChange}
                      className="w-full max-w-xs"
                      data-testid="install-prompt-button"
                    />
                  </div>

                  {/* Preview */}
                  <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
                      <Eye className="h-3 w-3" /> Preview
                    </p>
                    <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center">
                          <Building2 className="h-6 w-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-slate-900">{settings.install_prompt_title}</p>
                          <p className="text-sm text-slate-600">{settings.install_prompt_description}</p>
                        </div>
                        <button className="bg-orange-500 text-white px-4 py-2 rounded-lg text-sm font-medium">
                          {settings.install_prompt_button_text}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Feature Toggles */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
              <h3 className="text-lg font-bold text-slate-900 mb-4">Feature Toggles</h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b border-slate-100">
                  <div className="flex items-center gap-3">
                    <Wifi className="h-5 w-5 text-blue-500" />
                    <div>
                      <p className="font-medium text-slate-900">Offline Mode</p>
                      <p className="text-sm text-slate-500">Cache pages for offline access</p>
                    </div>
                  </div>
                  <Toggle 
                    enabled={settings.offline_enabled} 
                    onToggle={() => handleToggle('offline_enabled')}
                  />
                </div>

                <div className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3">
                    <Bell className="h-5 w-5 text-purple-500" />
                    <div>
                      <p className="font-medium text-slate-900">Push Notifications</p>
                      <p className="text-sm text-slate-500">Allow push notification prompts</p>
                    </div>
                  </div>
                  <Toggle 
                    enabled={settings.push_notifications_enabled} 
                    onToggle={() => handleToggle('push_notifications_enabled')}
                  />
                </div>
              </div>
            </div>

            {/* App Identity */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
              <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2 mb-4">
                <Palette className="h-5 w-5 text-orange-500" />
                App Identity
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    App Name
                  </label>
                  <input
                    type="text"
                    name="app_name"
                    value={settings.app_name}
                    onChange={handleChange}
                    className="w-full"
                    data-testid="app-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Short Name (for home screen)
                  </label>
                  <input
                    type="text"
                    name="app_short_name"
                    value={settings.app_short_name}
                    onChange={handleChange}
                    className="w-full"
                    data-testid="app-short-name"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    App Description
                  </label>
                  <textarea
                    name="app_description"
                    value={settings.app_description}
                    onChange={handleChange}
                    rows={2}
                    className="w-full"
                    data-testid="app-description"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Theme Color
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      name="theme_color"
                      value={settings.theme_color}
                      onChange={handleChange}
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      name="theme_color"
                      value={settings.theme_color}
                      onChange={handleChange}
                      className="flex-1 font-mono"
                      data-testid="theme-color"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Background Color
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      name="background_color"
                      value={settings.background_color}
                      onChange={handleChange}
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      name="background_color"
                      value={settings.background_color}
                      onChange={handleChange}
                      className="flex-1 font-mono"
                      data-testid="background-color"
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary flex items-center gap-2 px-6"
            data-testid="save-pwa-settings"
          >
            {saving ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Save className="h-5 w-5" />
            )}
            Save Settings
          </button>
        </div>
      </main>
    </div>
  );
}
